import { Component, ElementRef, HostBinding, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core'
import { DashboardRow } from './dvm-tool-team-dashboard.component'

export interface PracticeProgressStats {
   practiceName: string
   /** active employees (excludes approved deferrals and LOAINACTIVE) */
   total: number
   /** reviews where both TEAM and HM have submitted (or isCompleted) */
   completed: number
   teamSubmitted: number
   hmSubmitted: number
   /** reviews with at least one side submitted — use for "reviews in progress" */
   inProgress: number
   deferredCount: number
   /** employees in this practice with a ReviewType selected */
   reviewTypeSelected: number
}

@Component({
   selector: 'dvm-tool-hospital-progress-panel',
   templateUrl: './hospital-progress-panel.component.html',
   styleUrls: ['./hospital-progress-panel.component.scss'],
   standalone: false,
})
export class HospitalProgressPanelComponent implements OnChanges {
   @Input() rows: DashboardRow[] = []
   @Input() userName: string | null = null

   private static readonly STORAGE_KEY = 'ppv.hospitalProgressPanel.minimized'

   minimized = false
   practiceStats: PracticeProgressStats[] = []

   /** % of active employees (across scoped practices) with a ReviewType selected - "Rosters Complete" */
   overallReviewTypePct = 0
   /** % of reviews complete (both sides submitted, or isCompleted) - "Completed Reviews" */
   overallPct = 0

   @HostBinding('class.hpp-host--minimized')
   get hostMinimized(): boolean {
      return this.minimized
   }

   constructor(private elementRef: ElementRef) {
      this.minimized = true
   }

   @HostListener('document:click', ['$event'])
   onDocumentClick(event: MouseEvent): void {
      if (!this.minimized && !this.elementRef.nativeElement.contains(event.target)) {
         this.minimized = true
      }
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['rows'] || changes['userName']) this.buildStats()
   }

   toggleMinimized(): void {
      this.minimized = !this.minimized
   }

   trackByPractice(_index: number, stat: PracticeProgressStats): string {
      return stat.practiceName
   }

   private buildStats(): void {
      const byPractice = new Map<string, DashboardRow[]>()

      for (const row of this.scopeToHomePractices(this.rows ?? [])) {
         const practice = row.practice || 'Unassigned'
         if (!byPractice.has(practice)) byPractice.set(practice, [])
         byPractice.get(practice)!.push(row)
      }

      const stats: PracticeProgressStats[] = []

      byPractice.forEach((practiceRows, practiceName) => {
         const deferredCount = practiceRows.filter((r) => this.isDeferredRow(r)).length

         // Active reviews only: approved deferrals and on-leave users don't count toward totals
         const activeRows = practiceRows.filter((r) => !this.isDeferredRow(r) && r.revieweeUserStatus !== 'LOAINACTIVE')

         const teamDone = (r: DashboardRow) => this.hasSubmitted(r.isDVMSubmitted, r.dvmSubmittedOn)
         const hmDone = (r: DashboardRow) => this.hasSubmitted(r.isCOSSubmitted, r.cosSubmittedOn)

         stats.push({
            practiceName,
            total: activeRows.length,
            completed: activeRows.filter((r) => r.isCompleted || (teamDone(r) && hmDone(r))).length,
            teamSubmitted: activeRows.filter(teamDone).length,
            hmSubmitted: activeRows.filter(hmDone).length,
            inProgress: activeRows.filter((r) => teamDone(r) || hmDone(r)).length,
            deferredCount,
            reviewTypeSelected: activeRows.filter((r) => this.hasReviewType(r)).length,
         })
      })

      stats.sort((a, b) => a.practiceName.localeCompare(b.practiceName))
      this.practiceStats = stats

      const overallTotal = stats.reduce((sum, s) => sum + s.total, 0)
      const overallCompleted = stats.reduce((sum, s) => sum + s.completed, 0)
      const overallReviewTypeSelected = stats.reduce((sum, s) => sum + s.reviewTypeSelected, 0)

      this.overallPct = overallTotal > 0 ? Math.round((overallCompleted / overallTotal) * 100) : 0
      this.overallReviewTypePct = overallTotal > 0 ? Math.round((overallReviewTypeSelected / overallTotal) * 100) : 0
   }

   private hasReviewType(row: DashboardRow): boolean {
      return row.reviewType != null && String(row.reviewType).trim() !== ''
   }

   /**
    * If the logged-in user is the HM of any practice (row.hmName match, same as the
    * roster's home icon), return only rows belonging to those home practices —
    * including rows within them whose hmName differs (e.g. cross-covered members).
    * If they have no home practices, return all rows unchanged.
    */
   private scopeToHomePractices(rows: DashboardRow[]): DashboardRow[] {
      const name = (this.userName ?? '').trim()
      if (!name) return rows

      const homePractices = new Set<string>()
      for (const row of rows) {
         if ((row.hmName ?? '').trim() === name) homePractices.add(row.practice || 'Unassigned')
      }
      if (homePractices.size === 0) return rows

      return rows.filter((r) => homePractices.has(r.practice || 'Unassigned'))
   }

   private isDeferredRow(row: DashboardRow): boolean {
      return !!row.isDeferred && row.deferralStatus != 'Pending' && row.deferralStatus != 'Rejected' && row.deferralStatus != 'ReEnabled'
   }

   private hasSubmitted(isSubmitted: boolean | null | undefined, submittedOn: any): boolean {
      if (isSubmitted === true) return true
      if (submittedOn == null) return false
      if (typeof submittedOn === 'string' && (submittedOn.trim() === '' || submittedOn.trim().toLowerCase() === 'null')) return false
      return true
   }
}
